import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useBlocker, useLocation, useParams } from 'react-router-dom';
import { skillPackageApi } from '../api/client';
import { Button, Dialog, EmptyState, FormField, Icon, Tabs } from '../components/ui';
import ReactMarkdown from 'react-markdown';
import { safeMarkdownComponents } from '../components/SafeMarkdown';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import './SkillPackagesPage.css';
import SkillBuilder from '../components/skills/SkillBuilder';
import OutputSelector from '../components/OutputSelector';
import BindingSettingsDialog from '../components/skills/BindingSettingsDialog';
import PurposeSettingsDialog from '../components/skills/PurposeSettingsDialog';
import CreateSkillDialog from '../components/skills/CreateSkillDialog';
import { SkillSelect } from '../components/skills/SkillControls';

function errorText(error) {
  const detail = error?.response?.data?.error?.message || error?.response?.data?.detail || error.message;
  if (Array.isArray(detail)) return detail.map(item => item.msg || String(item)).join('\n');
  return typeof detail === 'string' ? detail : 'Не удалось выполнить действие';
}

function encode(bytes) {
  let text = '';
  for (let i = 0; i < bytes.length; i += 8192) text += String.fromCharCode(...bytes.subarray(i, i + 8192));
  return btoa(text);
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = filename; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const PackageEditor = forwardRef(function PackageEditor({ api, identity, onChanged }, ref) {
  const { confirm } = useUI();
  const [detail, setDetail] = useState(null);
  const [path, setPath] = useState('SKILL.md');
  const [content, setContent] = useState('');
  const [binary, setBinary] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState('edit');
  const [fileDialog, setFileDialog] = useState(false);
  const [newPath, setNewPath] = useState('references/fields-guide.md');
  const [version, setVersion] = useState('');
  const [versionFiles, setVersionFiles] = useState([]);
  const [comparison, setComparison] = useState(null);
  const revision = useRef(1);
  const savedContent = useRef('');
  const latestContent = useRef('');
  const savingPromise = useRef(null);
  const navigating = useRef(false);
  const fileInput = useRef(null);
  const zipInput = useRef(null);

  const read = useCallback(async (nextPath, versionId = '') => {
    const bytes = await api.read(identity, nextPath, versionId || undefined);
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      if (text.includes('\0')) throw new Error('binary');
      setContent(text); savedContent.current = text; latestContent.current = text; setBinary(false);
    } catch {
      setContent(''); savedContent.current = ''; latestContent.current = ''; setBinary(true);
    }
    setPath(nextPath); setDirty(false); setTab('edit');
  }, [api, identity]);

  const reload = useCallback(async () => {
    const next = await api.detail(identity);
    revision.current = next.draft.revision; setDetail(next);
    return next;
  }, [api, identity]);

  useEffect(() => {
    let active = true;
    api.detail(identity).then(async next => {
      if (!active) return;
      revision.current = next.draft.revision; setDetail(next);
      await read('SKILL.md');
      if (active) setInitialLoading(false);
    }).catch(e => active && setError(errorText(e)));
    return () => { active = false; };
  }, [api, identity, read]);

  useEffect(() => {
    const beforeUnload = event => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  const save = useCallback(async () => {
    if (savingPromise.current) return savingPromise.current;
    if (version || binary || latestContent.current === savedContent.current) return;
    const operation = async () => {
      setSaving(true);
      try {
        do {
          const snapshot = latestContent.current;
          const next = await api.edit(identity, revision.current, { [path]: encode(new TextEncoder().encode(snapshot)) });
          revision.current = next.revision; savedContent.current = snapshot;
          setDetail(current => ({ ...current, draft: next }));
        } while (latestContent.current !== savedContent.current);
        setDirty(false);
      } finally { setSaving(false); savingPromise.current = null; }
    };
    savingPromise.current = operation();
    return savingPromise.current;
  }, [api, identity, version, binary, path]);
  useImperativeHandle(ref, () => ({ save }), [save]);
  const blocker = useBlocker(dirty || saving);
  useEffect(() => {
    if (blocker.state !== 'blocked' || navigating.current) return;
    navigating.current = true;
    save().then(() => blocker.proceed()).catch(e => { setError(errorText(e)); blocker.reset(); })
      .finally(() => { navigating.current = false; });
  }, [blocker, save]);

  const run = async action => {
    setBusy(true); setError(''); setNotice('');
    try { await action(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const openFile = nextPath => run(async () => { await save(); await read(nextPath, version); });
  const files = version ? versionFiles : detail?.draft.files || [];

  if (!detail || initialLoading) return <div className="card skill-editor-empty">{error ? <p role="alert">{error}</p> : 'Загрузка скилла…'}</div>;
  const readonly = Boolean(version || detail.package.archived);
  return <section className="card skill-package-editor" aria-label="Редактор скилла">
    <div className="skill-editor-heading"><div><h2>{detail.package.title}</h2><span className="text-muted">{version ? 'Опубликованная версия' : dirty ? 'Есть несохранённые изменения' : 'Черновик сохранён'}</span></div>
      <div className="skill-editor-actions">
        <Button disabled={busy || readonly || !dirty} onClick={() => run(save)}>Сохранить</Button>
        <Button disabled={busy || readonly} onClick={() => run(async () => { await save(); const result = await api.validate(identity); setNotice(result.valid ? 'Пакет готов к публикации' : result.errors.join('\n')); })}>Проверить</Button>
        <Button variant="primary" disabled={busy || readonly} onClick={() => run(async () => { await save(); const result = await api.publish(identity, revision.current); await reload(); onChanged(); setNotice(`Опубликована версия ${result.version}. Она применяется в новых исследованиях.`); })}>Опубликовать</Button>
      </div></div>
    {error && <div role="alert" className="alert alert-error">{error}</div>}
    {notice && <div role="status" className="alert skill-notice">{notice}</div>}
    <div className="skill-file-toolbar">
      <label className="skill-history-label">Версия <SkillSelect value={version} disabled={busy} onChange={event => run(async () => {
        const nextVersion = event.target.value; await save();
        const info = nextVersion ? await api.version(identity, nextVersion) : null;
        setVersion(nextVersion); setVersionFiles(info?.files || []);
        await read('SKILL.md', nextVersion);
      })}><option value="">Черновик</option>{detail.versions.slice().reverse().map(v => <option key={v.id} value={v.id}>Версия {v.version} · {new Date(v.created_at).toLocaleDateString('ru')}</option>)}</SkillSelect></label>
      <Button disabled={busy || readonly} onClick={() => setFileDialog(true)}>Новый файл</Button>
      <Button disabled={busy || readonly} onClick={() => fileInput.current.click()}>Загрузить файлы</Button>
      <Button disabled={busy || readonly} onClick={() => zipInput.current.click()}>Импорт ZIP</Button>
      <Button disabled={busy || !detail.package.current_version_id} onClick={() => run(async () => download(await api.export(identity, version || detail.package.current_version_id), 'skill.zip'))}>Экспорт ZIP</Button>
      <input ref={fileInput} type="file" multiple hidden onChange={event => { const selected = [...event.target.files]; event.target.value = ''; run(async () => {
        await save(); const changes = {};
        for (const file of selected) {
          if (file.size > 25 * 1024 * 1024) throw new Error('Файл превышает 25 MiB');
          changes[file.webkitRelativePath || file.name] = encode(new Uint8Array(await file.arrayBuffer()));
        }
        await api.edit(identity, revision.current, changes); await reload(); await read(path);
      }); }} />
      <input ref={zipInput} type="file" accept=".zip" hidden onChange={event => { const file = event.target.files[0]; event.target.value = ''; if (file) run(async () => {
        await save(); if (!await confirm('Заменить содержимое черновика файлами из архива?')) return;
        await api.import(identity, revision.current, file); await reload(); await read('SKILL.md');
      }); }} />
    </div>
    <div className="skill-files-layout">
      <nav className="skill-file-tree" aria-label="Файлы пакета">{files.map(file => <button type="button" key={file.path} aria-current={path === file.path ? 'page' : undefined} disabled={busy}
        className={path === file.path ? 'active' : ''} onClick={() => openFile(file.path)}><Icon name="document" size={16} /><span>{file.path}</span></button>)}</nav>
      <div className="skill-file-pane"><div className="skill-file-title"><strong>{path}</strong><div className="skill-editor-actions">
        {!readonly && <Button disabled={busy} onClick={() => { setNewPath(path); setFileDialog('rename'); }}>Переименовать</Button>}
        {!readonly && path !== 'SKILL.md' && <Button disabled={busy} onClick={() => run(async () => { if (!await confirm(`Удалить ${path} из черновика?`)) return;
          await save(); await api.edit(identity, revision.current, { [path]: null }); await reload(); await read('SKILL.md');
        })}>Удалить</Button>}
        {!version && detail.package.current_version_id && <Button disabled={busy || binary} onClick={() => run(async () => {
          let previous = '';
          try { previous = new TextDecoder().decode(await api.read(identity, path, detail.package.current_version_id)); }
          catch (e) { if (e.response?.status !== 404) throw e; }
          setComparison(previous);
        })}>Сравнить</Button>}
      </div></div>
      {binary ? <div className="skill-editor-empty"><p>Ресурс пакета. Для просмотра скачай файл.</p><Button onClick={() => run(async () => download(new Blob([await api.read(identity, path, version || undefined)]), path.split('/').pop()))}>Скачать файл</Button></div> : <>
        {path.endsWith('.md') && <Tabs items={[{ key: 'edit', label: 'Текст' }, { key: 'preview', label: 'Предпросмотр' }]} value={tab} onChange={setTab} label="Режим просмотра" />}
        {tab === 'preview' ? <div className="skill-markdown-preview" onClickCapture={event => {
          const target = event.target.closest('a'); const href = target?.getAttribute('href');
          if (href && files.some(file => file.path === href)) { event.preventDefault(); openFile(href); }
        }}><ReactMarkdown components={safeMarkdownComponents}>{content}</ReactMarkdown></div> : <textarea aria-label={`Содержимое ${path}`} spellCheck={false} className="skill-code-editor" value={content} readOnly={readonly || busy || saving}
          onChange={event => { latestContent.current = event.target.value; setContent(event.target.value); setDirty(event.target.value !== savedContent.current); }} />}
      </>}
      </div>
    </div>
    {fileDialog && <Dialog title={fileDialog === 'rename' ? 'Переименовать файл' : 'Новый файл'} onClose={() => setFileDialog(false)}>
      <FormField label="Путь внутри пакета" hint="Например, references/fields-guide.md или scripts/calculate.py"><input autoFocus value={newPath} onChange={e => setNewPath(e.target.value)} /></FormField>
      <div className="dialog-actions"><Button onClick={() => setFileDialog(false)}>Отмена</Button><Button variant="primary" disabled={busy || !newPath.trim()} onClick={() => run(async () => {
        await save(); const target = newPath.trim(); if (files.some(file => file.path === target)) throw new Error('Файл с таким путём уже существует');
        const changes = { [target]: fileDialog === 'rename' ? encode(new Uint8Array(await api.read(identity, path))) : '' };
        if (fileDialog === 'rename') changes[path] = null;
        await api.edit(identity, revision.current, changes); await reload(); await read(target); setFileDialog(false);
      })}>{fileDialog === 'rename' ? 'Переименовать' : 'Создать'}</Button></div>
    </Dialog>}
    {comparison !== null && <Dialog title={`Изменения: ${path}`} className="skill-diff-dialog" onClose={() => setComparison(null)}>
      <div className="skill-diff"><div><h3>Опубликовано</h3><pre>{comparison || 'Файла не было'}</pre></div><div><h3>Черновик</h3><pre>{content}</pre></div></div>
    </Dialog>}
  </section>;
});

export default function SkillPackagesPage() {
  const { projectId } = useParams();
  const location = useLocation();
  const admin = location.pathname.startsWith('/admin/');
  const { user } = useAuth();
  const { confirm } = useUI();
  const scope = admin ? 'admin' : projectId ? `projects/${projectId}` : 'me';
  const api = useMemo(() => skillPackageApi(scope), [scope]);
  const [packages, setPackages] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [advanced, setAdvanced] = useState(admin);
  const [outputDefaults, setOutputDefaults] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('packages');
  const [binding, setBinding] = useState(null);
  const [purposeEditor, setPurposeEditor] = useState(null);
  const editor = useRef(null);
  const reload = useCallback(async () => {
    const [items, functions, assignments, own] = await Promise.all([api.list(), api.purposes(), api.bindings(), projectId ? skillPackageApi('me').list() : Promise.resolve([])]);
    setPackages(items); setPurposes(functions); setBindings(assignments); setPersonal(own); setLoading(false);
  }, [api, projectId]);
  useEffect(() => { setSelected(null); setOutputDefaults(null); setLoading(true); reload().catch(e => { setError(errorText(e)); setLoading(false); }); }, [reload]);
  if (admin && !user?.is_admin) return <Navigate to="/my-skills" replace />;
  const run = async action => { setError(''); try { await action(); } catch (e) { setError(errorText(e)); } };
  const options = [...new Map([...personal, ...packages].map(p => [p.id, p])).values()].filter(p => !p.archived && p.current_version_id);
  const selectedPackage = packages.find(p => p.id === selected);
  const methodology = Boolean(selectedPackage?.methodology_skill_id || selectedPackage?.role === 'methodology');
  const fileEditor = advanced || methodology;
  return <div className="skill-packages-page">
    <div className="page-header"><div>{projectId && <Link className="text-muted" to={`/projects/${projectId}`}>← К проекту</Link>}
      <h1 className="page-title">{admin ? 'Базовые скиллы' : projectId ? 'Скиллы проекта' : 'Мои скиллы'}</h1>
      <p className="text-muted">{admin ? 'Инструкции, методики и ресурсы агентов. Настрой назначения и базовый состав.' : 'Опишите правила работы или создайте шаблон результата по своему образцу.'}</p></div>
      <div className="skill-editor-actions">{projectId && <Link className="btn" to="/my-skills">Личные скиллы</Link>}
        {admin && <Button onClick={() => setPurposeEditor({ id: '', title: '', description: '', agents: [] })}>Новое назначение</Button>}
        <Button variant="primary" onClick={() => setCreating(true)}>Создать скилл</Button></div></div>
    {error && <div role="alert" className="alert alert-error">{error}</div>}
    <Tabs items={[{ key: 'packages', label: 'Скиллы' }, { key: 'bindings', label: 'Применение' }, ...(!admin ? [{ key: 'defaults', label: 'Результаты по умолчанию' }] : [])]} value={tab} onChange={value => run(async () => { await editor.current?.save(); setTab(value); })} label="Раздел скиллов" />
    <div className="skill-page-body">{loading ? <p>Загрузка…</p> : tab === 'defaults' ? <div className="card"><OutputSelector projectId={projectId} value={outputDefaults} onChange={setOutputDefaults} settings /></div> : tab === 'packages' ? <div className="skill-studio-layout">
      <aside className="skill-package-list" aria-label="Каталог скиллов" tabIndex={0}>{packages.filter(p => !p.archived).map(p => <div className={`card skill-package-card ${selected === p.id ? 'is-selected' : ''}`} key={p.id}>
        <button className="skill-package-select" onClick={() => run(async () => { await editor.current?.save(); setSelected(p.id); })}><Icon name="skills" /><strong>{p.title}</strong><span className="text-muted">{p.current_version_id ? 'Опубликован' : 'Черновик'}{p.methodology_skill_id ? ' · Методика' : ''}</span></button>
        <Button onClick={() => run(async () => { if (!await confirm(`Архивировать «${p.title}»? Выполняющиеся исследования сохранят свои версии.`)) return;
          await editor.current?.save(); await api.archive(p.id); if (selected === p.id) setSelected(null); await reload();
        })}>Архивировать</Button></div>)}
        {!packages.some(p => !p.archived) && <EmptyState title="Создай первый скилл">Опишите требования или загрузите образец результата.</EmptyState>}
      </aside>
      {selected ? <div className="skill-builder-container" role="region" aria-label="Редактор и настройки скилла" tabIndex={0}>{!methodology && <div className="skill-editor-actions"><Button onClick={() => run(async () => { await editor.current?.save(); setAdvanced(value => !value); })}>{advanced ? 'Простой конструктор' : 'Расширенный редактор'}</Button></div>}{fileEditor ? <PackageEditor key={`${scope}:${selected}:advanced`} ref={editor} api={api} identity={selected} onChanged={() => run(reload)} /> : <SkillBuilder key={`${scope}:${selected}:simple`} ref={editor} api={api} identity={selected} purposes={purposes} onChanged={reload} />}</div> : <div className="card skill-editor-empty"><Icon name="skills" size={36} /><h2>Инструкция и всё необходимое рядом</h2><p className="text-muted">Выбери скилл или создай новый, чтобы добавить файлы и опубликовать версию.</p></div>}
    </div> : <div className="skill-purpose-grid">{purposes.map(purpose => {
      const assignment = bindings.find(b => b.purpose_id === purpose.id);
      return <section className="card skill-purpose-card" key={purpose.id}><div className="skill-purpose-heading"><span className="skill-choice-icon"><Icon name={purpose.can_replace_form ? 'document' : 'skills'} /></span><div><h2>{purpose.title}</h2><p className="text-muted">{purpose.description}</p></div></div>
        {assignment?.policy_notice && <p role="status">{assignment.policy_notice}</p>}
        <div className={`skill-purpose-state ${assignment ? 'has-custom' : ''}`}><span className="badge">{assignment ? assignment.mode === 'replace' ? admin ? 'Основной состав' : 'Свой шаблон' : 'Дополнительные правила' : 'По умолчанию'}</span><span>{projectId ? 'Для проекта' : admin ? 'Базовая настройка' : 'Личная настройка'}</span></div>
        {assignment ? <ul className="skill-purpose-selected">{assignment.package_ids.map(id => <li key={id}><Icon name="check" size={16} />{options.find(p => p.id === id)?.title || 'Скилл недоступен — выберите другой'}</li>)}</ul> : <p className="text-muted">{projectId ? 'Наследуются личные настройки, затем стандартные.' : admin ? 'Базовый состав не задан.' : 'Действуют стандартные правила и шаблоны.'}</p>}
        <div className="skill-editor-actions"><Button onClick={() => setBinding({ purpose, mode: assignment?.mode || 'inherit', package_ids: assignment?.package_ids || [] })}>Настроить</Button>
          {assignment && <Button onClick={() => run(async () => { await api.unbind(purpose.id); await reload(); })}>{admin ? 'Убрать назначение' : 'Наследовать'}</Button>}
          {admin && <Button onClick={() => setPurposeEditor({ ...purpose, existing: true })}>Изменить назначение</Button>}
        </div></section>;
    })}</div>}</div>
    {creating && <CreateSkillDialog admin={admin} projectId={projectId} onClose={() => setCreating(false)} onCreate={async (title, resultKind) => {
      await editor.current?.save(); const next = await api.create(title, resultKind);
      setSelected(next.id); setAdvanced(admin); setTab('packages'); setCreating(false); await run(reload);
    }} />}
    {binding && <BindingSettingsDialog binding={binding} packages={options} admin={admin} projectId={projectId} onClose={() => setBinding(null)} onSave={async (mode, ids) => {
      if (mode === 'inherit') await api.unbind(binding.purpose.id);
      else await api.bind(binding.purpose.id, mode, ids);
      await reload(); setBinding(null);
    }} />}
    {purposeEditor && <PurposeSettingsDialog purpose={purposeEditor} onClose={() => setPurposeEditor(null)} onSave={async (id, values) => {
      await api.savePurpose(id, values); await reload(); setPurposeEditor(null);
    }} />}
  </div>;
}
